<template>
  <div class="admin-step-up-page flex items-center justify-center min-h-[70vh] p-4">
    <div
      class="w-full max-w-md surface-card border-round shadow-2 p-6 flex flex-col gap-4"
      data-testid="admin-step-up-panel"
    >
      <h1 class="text-xl font-semibold text-[var(--color-dark,#1E2019)] m-0">
        {{ $t('AdminStepUp.title') }}
      </h1>
      <p class="text-sm text-gray-600 m-0">
        {{ $t('AdminStepUp.subtitle') }}
      </p>

      <Message v-if="infoMessage" severity="info" :closable="false" class="text-sm">
        {{ infoMessage }}
      </Message>
      <Message v-if="errorMessage" severity="error" :closable="false" class="text-sm">
        {{ errorMessage }}
      </Message>

      <div>
        <label class="block text-sm font-medium mb-1" for="admin-step-up-otp">
          {{ $t('AdminStepUp.otpLabel') }}
        </label>
        <InputText
          id="admin-step-up-otp"
          v-model="otp"
          class="w-full font-mono text-lg tracking-widest"
          maxlength="6"
          inputmode="numeric"
          autocomplete="one-time-code"
          data-testid="admin-step-up-otp"
          :disabled="verifying"
          @keyup.enter="submit"
        />
      </div>

      <div class="flex flex-wrap gap-2 justify-between items-center">
        <Button
          type="button"
          :label="$t('AdminStepUp.resend')"
          severity="secondary"
          text
          :loading="sending"
          :disabled="verifying"
          data-testid="admin-step-up-resend"
          @click="sendOtp"
        />
        <Button
          type="button"
          :label="$t('AdminStepUp.verify')"
          icon="pi pi-check"
          :loading="verifying"
          :disabled="otp.trim().length !== 6 || sending"
          data-testid="admin-step-up-verify"
          @click="submit"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Message from 'primevue/message';
import NetworkAuth from '@/helpers/networking/NetworkAuth';
import { TENANT_ROLE_ADMIN } from '@/utils/userRoles';

export default {
  name: 'AdminStepUpView',
  components: { Button, InputText, Message },
  data() {
    return {
      otp: '',
      sending: false,
      verifying: false,
      infoMessage: null,
      errorMessage: null,
    };
  },
  async mounted() {
    const roles = this.$store.getters.getUserRoles || [];
    const isAdmin =
      roles.includes(TENANT_ROLE_ADMIN) || this.$store.getters.getPermissions?.admin;
    // Roles can be empty before ping (e.g. OAuth bounce); let send-otp / backend decide.
    if (roles.length > 0 && !isAdmin) {
      this.$router.replace('/');
      return;
    }
    await this.sendOtp();
  },
  methods: {
    async sendOtp() {
      this.sending = true;
      this.errorMessage = null;
      try {
        const { data } = await NetworkAuth.sendAdminStepUpOtp();
        this.infoMessage = data?.message || this.$t('AdminStepUp.codeSent');
      } catch (err) {
        this.errorMessage =
          err?.response?.data?.message || err?.message || this.$t('AdminStepUp.sendFailed');
      } finally {
        this.sending = false;
      }
    },
    async submit() {
      const code = String(this.otp || '').trim();
      if (code.length !== 6) return;
      this.verifying = true;
      this.errorMessage = null;
      try {
        const { data } = await NetworkAuth.verifyAdminStepUpOtp({ otp: code });
        if (!data?.token || !data?.refresh_token) {
          throw new Error(this.$t('AdminStepUp.verifyFailed'));
        }
        this.$store.commit('setToken', data.token);
        this.$store.commit('setRefreshToken', data.refresh_token);

        const oauthPending = this.$route.query.oauth_pending;
        if (oauthPending) {
          const { data: oauthData } = await NetworkAuth.completeMcpOAuth(oauthPending, {
            headers: { Authorization: `Bearer ${data.token}` },
          });
          if (!oauthData?.handoffId) {
            throw new Error(this.$t('AdminStepUp.verifyFailed'));
          }
          sessionStorage.setItem('mcp_oauth_handoff', oauthData.handoffId);
          await this.$router.replace({ name: 'mcpOAuthSuccess' });
          return;
        }
        const redirect = this.$route.query.redirect || '/contentManagement';
        await this.$router.replace(String(redirect));
      } catch (err) {
        this.errorMessage =
          err?.response?.data?.message || err?.message || this.$t('AdminStepUp.verifyFailed');
      } finally {
        this.verifying = false;
      }
    },
  },
};
</script>
